import { makeAutoObservable } from 'mobx'
import { ToastData } from '../types/ToastData'
import { v4 as uuidv4 } from 'uuid'
import { SortOrder } from '../types/SortOrder'
import { ReactElement } from 'react'
import { LocalStorageKeys } from '../types/LocalStorage'

type ConfirmationModal = {
    title: string
    body: ReactElement
    action?: () => Promise<void>
    question?: string
    isDisabled?: boolean
    hideAction?: boolean
    formId?: string
    shouldNotCloseAfterAction?: boolean
}

type RightSideBarData = { title: string; value: any; component: ReactElement }
type CreateSgmentModalOptions = {
    onAction?: (name?: string) => void
    message?: ReactElement
    submitButtonLabel?: string
    hideCloneActions?: boolean
}

export type Theme = 'light' | 'dark'

export const DARK_THEME_CLASS = 'anon-theme-dark'

export class AppStore {
    isSidebarClosed = localStorage[LocalStorageKeys.isSidebarClosed] === 'true' || localStorage[LocalStorageKeys.isSidebarClosed] === undefined
    theme: Theme = localStorage[LocalStorageKeys.theme] === 'dark' ? 'dark' : 'light'
    isOpenRightSidebar = false
    isOpenCreateSegmentModal = false
    createSgmentModalOptions?: CreateSgmentModalOptions
    isCreateRetargetingSegment = false
    isCurrentlyEstimated = false
    isTourOpen = false
    toastNotifications: ToastData[] = []
    confirmationModal: ConfirmationModal | null = null
    sidebarData: RightSideBarData | null = null
    sortBy: { value: string; order: SortOrder.ASC | SortOrder.DESC; customOrder?: any[]; isSameOrder?: boolean; oldOrder?: any[] } = null
    isMessageUpdated: boolean = false
    pendingRequests = 0

    constructor() {
        makeAutoObservable(this)
        document.body.classList.toggle(DARK_THEME_CLASS, this.theme === 'dark')
    }

    toggleTheme() {
        this.theme = this.theme === 'dark' ? 'light' : 'dark'
        localStorage[LocalStorageKeys.theme] = this.theme
        document.body.classList.toggle(DARK_THEME_CLASS, this.theme === 'dark')
    }

    setIsCurrentlyEstimated(isEstimated: boolean) {
        this.isCurrentlyEstimated = isEstimated
    }

    setIsTourOpen(isOpen: boolean) {
        this.isTourOpen = isOpen
    }

    checkMessageUpdated(isUpdated: boolean) {
        this.isMessageUpdated = isUpdated
    }

    setConfirmationModalDisabled(isDisabled: boolean) {
        this.confirmationModal.isDisabled = isDisabled
    }

    setConfirmationModal(confirmationModal: ConfirmationModal | null) {
        this.confirmationModal = confirmationModal
    }

    setToastNotification(toastNotification: ToastData) {
        toastNotification.id = uuidv4()
        toastNotification.body = toastNotification.body ?? 'Something went wrong'
        toastNotification.isHidden = toastNotification.isHidden ?? false
        this.toastNotifications.push(toastNotification)
    }

    deleteToast(id: string) {
        const index = this.toastNotifications.findIndex((x) => x.id === id)
        if (index >= 0) {
            this.toastNotifications.splice(index, 1)
        }
    }

    setCreateSegmentModal(isOpen: boolean, options?: CreateSgmentModalOptions) {
        this.isOpenCreateSegmentModal = isOpen
        this.createSgmentModalOptions = isOpen ? options : undefined
    }

    setCreateRetargetingSegment(isOpen: boolean) {
        this.isCreateRetargetingSegment = isOpen
    }

    setSidebarData(data: RightSideBarData) {
        this.sidebarData = data
    }

    setSortBy(sortBy: { value: string; order: SortOrder.ASC | SortOrder.DESC; customOrder?: any[]; isSameOrder?: boolean; oldOrder?: any[] }) {
        this.sortBy = sortBy
    }

    toggleSidebar() {
        this.isSidebarClosed = !this.isSidebarClosed
        localStorage[LocalStorageKeys.isSidebarClosed] = this.isSidebarClosed ? 'true' : 'false'
    }

    toggleRightSidebar() {
        this.isOpenRightSidebar = !this.isOpenRightSidebar
    }

    incrementRequests() {
        this.pendingRequests++
    }
    decrementRequests() {
        this.pendingRequests--
    }
}
